import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { WealthTierKey, WEALTH_TIER_CONFIG } from '../utils/financeCalculations';
import { CharacterCosmetics, PublicDecorations } from '../types/myWealth';
import './FinanceWorldCanvas.css';

interface FinanceWorldCanvasProps {
  isPublicView?: boolean;
  tier?: WealthTierKey;
  hasWealthData?: boolean;
  characterCosmetics?: CharacterCosmetics;
  publicTheme?: 'classic' | 'modern' | 'cozy' | 'botanical' | 'synthwave';
  publicDecorations?: PublicDecorations;
  onOpenCustomizer?: () => void;
}

const ROOM_THEMES = {
  classic: {
    wallColor: 0x93c5fd,
    floorColor: 0x60a5fa,
    rugColor: 0x2563eb,
    lightColor: 0xfffbeb,
    ambientColor: 0xffffff,
    accentColor: 0x1d4ed8
  },
  modern: {
    wallColor: 0xe2e8f0,
    floorColor: 0x94a3b8,
    rugColor: 0x334155,
    lightColor: 0xffffff,
    ambientColor: 0xf1f5f9,
    accentColor: 0x0f172a
  },
  cozy: {
    wallColor: 0xfde68a,
    floorColor: 0xd97706,
    rugColor: 0xb45309,
    lightColor: 0xffedd5,
    ambientColor: 0xfef3c7,
    accentColor: 0x78350f
  },
  botanical: {
    wallColor: 0xa7f3d0,
    floorColor: 0x34d399,
    rugColor: 0x059669,
    lightColor: 0xecfdf5,
    ambientColor: 0xd1fae5,
    accentColor: 0x064e3b
  },
  synthwave: {
    wallColor: 0xd8b4fe,
    floorColor: 0xa855f7,
    rugColor: 0x7e22ce,
    lightColor: 0xfdf4ff,
    ambientColor: 0xfae8ff,
    accentColor: 0x581c87
  }
};

export const FinanceWorldCanvas: React.FC<FinanceWorldCanvasProps> = ({
  isPublicView = false,
  tier = 'starter',
  hasWealthData = true,
  characterCosmetics = {
    skinTone: '#f5cba7',
    hairStyle: 'short',
    hairColor: '#4a2e18',
    shirtColor: '#3498db',
    pantsColor: '#2c3e50',
    shoesColor: '#ffffff',
    accessory: 'none'
  },
  publicTheme = 'classic',
  publicDecorations = {
    rugColor: '#e74c3c',
    plantCount: 2,
    hasComputerDesk: true,
    hasBookshelf: true,
    hasAquarium: false,
    lightingMood: 'warm'
  },
  onOpenCustomizer
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [webglSupported, setWebglSupported] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentZone, setCurrentZone] = useState<string>('Living Lounge');
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Character movement states referenced in anim loop
  const charPosRef = useRef({ x: 0, z: 0, rotation: 0 });
  const charTargetRef = useRef<{ x: number; z: number } | null>(null);
  const keysPressedRef = useRef<{ [key: string]: boolean }>({});
  const cameraZoomRef = useRef<number>(1);
  const sceneElementsRef = useRef<{
    scene?: THREE.Scene;
    camera?: THREE.PerspectiveCamera;
    renderer?: THREE.WebGLRenderer;
    characterGroup?: THREE.Group;
    targetMarker?: THREE.Mesh;
    charParts?: {
      leftLeg: THREE.Mesh;
      rightLeg: THREE.Mesh;
      leftArm: THREE.Mesh;
      rightArm: THREE.Mesh;
      body: THREE.Group;
    };
  }>({});

  // Detect Mobile Viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // WebGL availability check
  const checkWebGL = () => {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      );
    } catch (e) {
      return false;
    }
  };

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent scrolling on arrow keys while inside game
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) {
        if (document.activeElement === canvasRef.current || containerRef.current?.contains(document.activeElement)) {
          e.preventDefault();
        }
      }
      keysPressedRef.current[e.key.toLowerCase()] = true;
      keysPressedRef.current[e.code] = true;
      // Clear tap target if user uses keyboard
      charTargetRef.current = null;
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysPressedRef.current[e.key.toLowerCase()] = false;
      keysPressedRef.current[e.code] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Three.js Scene Setup & Loop
  useEffect(() => {
    if (!checkWebGL()) {
      setWebglSupported(false);
      setLoading(false);
      return;
    }

    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    let animationFrameId: number;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 520;

    const showEmptyPlot = !isPublicView && (!hasWealthData || tier === 'empty');

    // Theme detection for canvas background
    const isDarkMode =
      typeof document !== 'undefined' &&
      (document.body.classList.contains('dark-mode') ||
        document.documentElement.classList.contains('dark-mode'));
    const bgColor = isDarkMode ? 0x18181b : 0xe2e8f0;

    // 1. Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(bgColor);
    scene.fog = new THREE.FogExp2(bgColor, 0.02);

    // 2. Camera (Angled isometric perspective)
    const camera = new THREE.PerspectiveCamera(38, width / height, 0.5, 100);
    const baseCamPos = new THREE.Vector3(14, 18, 14);
    camera.position.copy(baseCamPos);
    camera.lookAt(0, 0.8, 0);

    // 3. Renderer
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 4. Lighting & Environment Theme
    const tierInfo = WEALTH_TIER_CONFIG[tier] || WEALTH_TIER_CONFIG.starter;
    const theme = isPublicView
      ? ROOM_THEMES[publicTheme] || ROOM_THEMES.classic
      : {
          wallColor: parseInt(tierInfo.roomTheme.wallColor.replace('#', '0x'), 16),
          floorColor: parseInt(tierInfo.roomTheme.floorColor.replace('#', '0x'), 16),
          rugColor: parseInt(tierInfo.roomTheme.rugColor.replace('#', '0x'), 16),
          lightColor: 0xfffbeb,
          ambientColor: 0xffffff,
          accentColor: parseInt(tierInfo.accentColor.replace('#', '0x'), 16)
        };

    const ambientLight = new THREE.AmbientLight(theme.ambientColor, showEmptyPlot ? 0.95 : 0.75);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(theme.lightColor, 1.25);
    dirLight.position.set(10, 16, 12);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 1024;
    dirLight.shadow.mapSize.height = 1024;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 40;
    const shadowD = 10;
    dirLight.shadow.camera.left = -shadowD;
    dirLight.shadow.camera.right = shadowD;
    dirLight.shadow.camera.top = shadowD;
    dirLight.shadow.camera.bottom = -shadowD;
    scene.add(dirLight);

    // Interior Warm Floor Lamp Light (only when house walls exist)
    if (!showEmptyPlot) {
      const floorLamp = new THREE.PointLight(0xffb703, 0.8, 12);
      floorLamp.position.set(-4.5, 3.5, -4.5);
      scene.add(floorLamp);

      if (tier === 'premium') {
        const goldChandelier = new THREE.PointLight(0xf59e0b, 1.0, 16);
        goldChandelier.position.set(0, 4.2, 0);
        scene.add(goldChandelier);
      }
    }

    // 5. Construct Room Geometry (Procedural Blocky Low-Poly)
    const roomGroup = new THREE.Group();

    if (showEmptyPlot) {
      // ─────────────────────────────────────────────────────────────
      // EMPTY GROUND PLOT MODE: No wealth data entered
      // Shows ONLY the open ground plot platform with perimeter pavers
      // and the human character. No walls, no windows, no furniture.
      // ─────────────────────────────────────────────────────────────
      const groundGeo = new THREE.BoxGeometry(14, 0.4, 14);
      const groundMat = new THREE.MeshStandardMaterial({
        color: isDarkMode ? 0x27272a : 0x86efac, // pleasant lawn green in light mode, sleek slate ground in dark mode
        roughness: 0.85,
        metalness: 0.05
      });
      const groundMesh = new THREE.Mesh(groundGeo, groundMat);
      groundMesh.position.y = -0.2;
      groundMesh.receiveShadow = true;
      roomGroup.add(groundMesh);

      // Boundary curb / pavers outlining the property plot
      const curbMat = new THREE.MeshStandardMaterial({ color: isDarkMode ? 0x3f3f46 : 0x94a3b8 });
      const curbNorth = new THREE.Mesh(new THREE.BoxGeometry(14.2, 0.15, 0.3), curbMat);
      curbNorth.position.set(0, 0.05, -7);
      roomGroup.add(curbNorth);

      const curbSouth = new THREE.Mesh(new THREE.BoxGeometry(14.2, 0.15, 0.3), curbMat);
      curbSouth.position.set(0, 0.05, 7);
      roomGroup.add(curbSouth);

      const curbWest = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.15, 14.2), curbMat);
      curbWest.position.set(-7, 0.05, 0);
      roomGroup.add(curbWest);

      const curbEast = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.15, 14.2), curbMat);
      curbEast.position.set(7, 0.05, 0);
      roomGroup.add(curbEast);

      // Subtle ground grid markers
      const gridHelper = new THREE.GridHelper(
        14,
        14,
        isDarkMode ? 0x52525b : 0x4ade80,
        isDarkMode ? 0x3f3f46 : 0x86efac
      );
      gridHelper.position.y = 0.01;
      (gridHelper.material as THREE.Material).opacity = 0.35;
      (gridHelper.material as THREE.Material).transparent = true;
      roomGroup.add(gridHelper);
    } else {
      // ─────────────────────────────────────────────────────────────
      // CONSTRUCTED HOUSE MODE: Wealth data has been entered
      // Walls, floor, window, doorway and progressive furniture
      // ─────────────────────────────────────────────────────────────

      // Floor Base (14 x 14 blocky foundation)
      const floorGeo = new THREE.BoxGeometry(14, 0.4, 14);
      const floorMat = new THREE.MeshStandardMaterial({
        color: theme.floorColor,
        roughness: 0.6,
        metalness: 0.1
      });
      const floorMesh = new THREE.Mesh(floorGeo, floorMat);
      floorMesh.position.y = -0.2;
      floorMesh.receiveShadow = true;
      roomGroup.add(floorMesh);

      // Grid Floor Trim / Planks effect
      const gridHelper = new THREE.GridHelper(14, 14, 0xffffff, 0x000000);
      gridHelper.position.y = 0.01;
      (gridHelper.material as THREE.Material).opacity = 0.15;
      (gridHelper.material as THREE.Material).transparent = true;
      roomGroup.add(gridHelper);

      // Back Wall (Z = -7)
      const wallMat = new THREE.MeshStandardMaterial({
        color: theme.wallColor,
        roughness: 0.8
      });
      const backWallGeo = new THREE.BoxGeometry(14, 5, 0.4);
      const backWall = new THREE.Mesh(backWallGeo, wallMat);
      backWall.position.set(0, 2.5, -7);
      backWall.receiveShadow = true;
      roomGroup.add(backWall);

      // Left Wall (X = -7)
      const leftWallGeo = new THREE.BoxGeometry(0.4, 5, 14);
      const leftWall = new THREE.Mesh(leftWallGeo, wallMat);
      leftWall.position.set(-7, 2.5, 0);
      leftWall.receiveShadow = true;
      roomGroup.add(leftWall);

      // Wall Trim / Skirting Boards
      const trimMat = new THREE.MeshStandardMaterial({ color: 0x334155 });
      const trimBack = new THREE.Mesh(new THREE.BoxGeometry(14, 0.25, 0.45), trimMat);
      trimBack.position.set(0, 0.125, -6.95);
      roomGroup.add(trimBack);

      const trimLeft = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.25, 14), trimMat);
      trimLeft.position.set(-6.95, 0.125, 0);
      roomGroup.add(trimLeft);

      // Window on Back Wall with sky glow
      const windowFrameGeo = new THREE.BoxGeometry(3.6, 2.4, 0.5);
      const windowFrameMat = new THREE.MeshStandardMaterial({ color: 0x1e293b });
      const windowFrame = new THREE.Mesh(windowFrameGeo, windowFrameMat);
      windowFrame.position.set(-2, 3, -6.9);
      roomGroup.add(windowFrame);

      const glassGeo = new THREE.BoxGeometry(3.3, 2.1, 0.52);
      const glassMat = new THREE.MeshBasicMaterial({
        color: 0x93c5fd,
        transparent: true,
        opacity: 0.65
      });
      const glassMesh = new THREE.Mesh(glassGeo, glassMat);
      glassMesh.position.set(-2, 3, -6.9);
      roomGroup.add(glassMesh);

      // Doorway Frame on Left Wall
      const doorFrame = new THREE.Mesh(new THREE.BoxGeometry(0.5, 3.6, 1.8), trimMat);
      doorFrame.position.set(-6.9, 1.8, 3);
      roomGroup.add(doorFrame);

      const doorInner = new THREE.Mesh(
        new THREE.BoxGeometry(0.45, 3.4, 1.6),
        new THREE.MeshStandardMaterial({ color: 0x7c2d12 })
      );
      doorInner.position.set(-6.88, 1.7, 3);
      roomGroup.add(doorInner);

      // ─────────────────────────────────────────────────────────────
      // FURNISHINGS POPULATION BASED ON WEALTH TIER
      // ─────────────────────────────────────────────────────────────
      if (tier === 'negative') {
        // Reset & Rebuild Focus Setup
        const crate = new THREE.Mesh(
          new THREE.BoxGeometry(1.2, 0.85, 1.2),
          new THREE.MeshStandardMaterial({ color: 0x854d0e, roughness: 0.7 })
        );
        crate.position.set(-3.5, 0.42, -3.0);
        crate.castShadow = true;
        roomGroup.add(crate);

        const focusDesk = new THREE.Mesh(
          new THREE.BoxGeometry(2.4, 0.12, 1.2),
          new THREE.MeshStandardMaterial({ color: 0x475569, roughness: 0.5 })
        );
        focusDesk.position.set(3.5, 1.1, -5.2);
        focusDesk.castShadow = true;
        roomGroup.add(focusDesk);

        const focusLamp = new THREE.Mesh(
          new THREE.CylinderGeometry(0.04, 0.04, 1.0, 8),
          new THREE.MeshStandardMaterial({ color: 0x94a3b8 })
        );
        focusLamp.position.set(4.2, 1.6, -5.2);
        roomGroup.add(focusLamp);

        // 1 single resilient sprout in pot
        const pot = new THREE.Mesh(
          new THREE.CylinderGeometry(0.3, 0.2, 0.5, 8),
          new THREE.MeshStandardMaterial({ color: 0xd97706 })
        );
        pot.position.set(-5.6, 0.25, -5.2);
        pot.castShadow = true;
        roomGroup.add(pot);
        const sprout = new THREE.Mesh(
          new THREE.DodecahedronGeometry(0.4, 0),
          new THREE.MeshStandardMaterial({ color: 0x22c55e })
        );
        sprout.position.set(-5.6, 0.65, -5.2);
        sprout.castShadow = true;
        roomGroup.add(sprout);
      } else {
        // TIER: starter, building, comfortable, established, premium

        // Living Lounge Couch / Sofa (Starter and up)
        const sofaGroup = new THREE.Group();
        const sofaBase = new THREE.Mesh(
          new THREE.BoxGeometry(2.8, 0.5, 1.2),
          new THREE.MeshStandardMaterial({ color: 0x3b82f6 })
        );
        sofaBase.position.y = 0.25;
        sofaBase.castShadow = true;
        sofaGroup.add(sofaBase);

        const sofaBack = new THREE.Mesh(
          new THREE.BoxGeometry(2.8, 0.8, 0.35),
          new THREE.MeshStandardMaterial({ color: 0x2563eb })
        );
        sofaBack.position.set(0, 0.65, -0.45);
        sofaBack.castShadow = true;
        sofaGroup.add(sofaBack);

        const armLeft = new THREE.Mesh(
          new THREE.BoxGeometry(0.35, 0.6, 1.2),
          new THREE.MeshStandardMaterial({ color: 0x1d4ed8 })
        );
        armLeft.position.set(-1.3, 0.45, 0);
        armLeft.castShadow = true;
        sofaGroup.add(armLeft);

        const armRight = armLeft.clone();
        armRight.position.set(1.3, 0.45, 0);
        sofaGroup.add(armRight);

        sofaGroup.position.set(-3.5, 0, -3.2);
        roomGroup.add(sofaGroup);

        // Potted Plant #1 (starter+)
        const pot1 = new THREE.Mesh(
          new THREE.CylinderGeometry(0.35, 0.25, 0.6, 8),
          new THREE.MeshStandardMaterial({ color: 0xd97706 })
        );
        pot1.position.set(-5.8, 0.3, -1.2);
        pot1.castShadow = true;
        roomGroup.add(pot1);
        const leaf1 = new THREE.Mesh(
          new THREE.DodecahedronGeometry(0.55, 0),
          new THREE.MeshStandardMaterial({ color: 0x15803d, roughness: 0.7 })
        );
        leaf1.position.set(-5.8, 0.85, -1.2);
        leaf1.castShadow = true;
        roomGroup.add(leaf1);

        // Building Tier and above: Rug, Coffee Table + Mug, Floor Lamp, Plant #2
        if (['building', 'comfortable', 'established', 'premium'].includes(tier) || isPublicView) {
          const rugGeo = new THREE.BoxGeometry(4.2, 0.04, 3.2);
          const rugMat = new THREE.MeshStandardMaterial({ color: theme.rugColor, roughness: 0.9 });
          const rugMesh = new THREE.Mesh(rugGeo, rugMat);
          rugMesh.position.set(-3.5, 0.02, -2.5);
          rugMesh.receiveShadow = true;
          roomGroup.add(rugMesh);

          const coffeeTable = new THREE.Mesh(
            new THREE.BoxGeometry(1.6, 0.4, 0.9),
            new THREE.MeshStandardMaterial({ color: 0x78350f, roughness: 0.4 })
          );
          coffeeTable.position.set(-3.5, 0.2, -1.6);
          coffeeTable.castShadow = true;
          coffeeTable.receiveShadow = true;
          roomGroup.add(coffeeTable);

          const mug = new THREE.Mesh(
            new THREE.CylinderGeometry(0.08, 0.08, 0.16, 8),
            new THREE.MeshStandardMaterial({ color: 0xffffff })
          );
          mug.position.set(-3.3, 0.48, -1.6);
          roomGroup.add(mug);

          const lampPole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.04, 0.04, 3.2, 8),
            new THREE.MeshStandardMaterial({ color: 0x475569 })
          );
          lampPole.position.set(-5.6, 1.6, -5.2);
          roomGroup.add(lampPole);

          const lampShade = new THREE.Mesh(
            new THREE.CylinderGeometry(0.3, 0.5, 0.5, 8),
            new THREE.MeshStandardMaterial({ color: 0xfef08a, emissive: 0xfef08a, emissiveIntensity: 0.4 })
          );
          lampShade.position.set(-5.6, 3.2, -5.2);
          roomGroup.add(lampShade);

          // Plant #2
          const pot2 = pot1.clone();
          pot2.position.set(5.6, 0.3, 2.5);
          roomGroup.add(pot2);
          const leaf2 = leaf1.clone();
          leaf2.position.set(5.6, 0.85, 2.5);
          roomGroup.add(leaf2);
        }

        // Comfortable Tier and above: Computer Desk, Ultrawide Monitor, Keyboard, Office Chair, Plant #3
        const hasOffice =
          ['comfortable', 'established', 'premium'].includes(tier) ||
          (isPublicView && publicDecorations?.hasComputerDesk);

        if (hasOffice) {
          const deskGroup = new THREE.Group();
          const deskTop = new THREE.Mesh(
            new THREE.BoxGeometry(2.8, 0.15, 1.4),
            new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.3 })
          );
          deskTop.position.set(3.8, 1.3, -5.2);
          deskTop.castShadow = true;
          deskGroup.add(deskTop);

          const deskLegMat = new THREE.MeshStandardMaterial({ color: 0x0f172a });
          const leg1 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.3, 1.3), deskLegMat);
          leg1.position.set(2.55, 0.65, -5.2);
          leg1.castShadow = true;
          deskGroup.add(leg1);

          const leg2 = leg1.clone();
          leg2.position.set(5.05, 0.65, -5.2);
          deskGroup.add(leg2);

          const monitorScreen = new THREE.Mesh(
            new THREE.BoxGeometry(1.6, 0.85, 0.06),
            new THREE.MeshStandardMaterial({ color: 0x0284c7, emissive: 0x0284c7, emissiveIntensity: 0.5 })
          );
          monitorScreen.position.set(3.8, 2.05, -5.6);
          monitorScreen.castShadow = true;
          deskGroup.add(monitorScreen);

          const monitorStand = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.35, 0.2), deskLegMat);
          monitorStand.position.set(3.8, 1.55, -5.6);
          deskGroup.add(monitorStand);

          const keyboard = new THREE.Mesh(
            new THREE.BoxGeometry(0.65, 0.04, 0.22),
            new THREE.MeshStandardMaterial({ color: 0x334155 })
          );
          keyboard.position.set(3.8, 1.4, -4.8);
          deskGroup.add(keyboard);

          const chairSeat = new THREE.Mesh(
            new THREE.BoxGeometry(0.7, 0.12, 0.7),
            new THREE.MeshStandardMaterial({ color: 0x0284c7 })
          );
          chairSeat.position.set(3.8, 0.8, -4.0);
          chairSeat.castShadow = true;
          deskGroup.add(chairSeat);

          const chairBack = new THREE.Mesh(
            new THREE.BoxGeometry(0.7, 0.7, 0.1),
            new THREE.MeshStandardMaterial({ color: 0x0369a1 })
          );
          chairBack.position.set(3.8, 1.2, -3.65);
          deskGroup.add(chairBack);

          const chairPole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.8, 8), deskLegMat);
          chairPole.position.set(3.8, 0.4, -4.0);
          deskGroup.add(chairPole);

          roomGroup.add(deskGroup);

          // Plant #3
          const pot3 = pot1.clone();
          pot3.position.set(5.4, 0.3, 5.4);
          roomGroup.add(pot3);
          const leaf3 = leaf1.clone();
          leaf3.position.set(5.4, 0.85, 5.4);
          roomGroup.add(leaf3);
        }

        // Established Tier and above: Bookshelf + Books, Terrace Railing, Aquarium, Plant #4
        const hasBooks =
          ['established', 'premium'].includes(tier) ||
          (isPublicView && publicDecorations?.hasBookshelf);

        if (hasBooks) {
          const shelfGroup = new THREE.Group();
          const shelfFrame = new THREE.Mesh(
            new THREE.BoxGeometry(2.0, 4.0, 0.6),
            new THREE.MeshStandardMaterial({ color: 0x451a03 })
          );
          shelfFrame.position.set(5.5, 2.0, -6.6);
          shelfFrame.castShadow = true;
          shelfGroup.add(shelfFrame);

          const bookColors = [0xef4444, 0x10b981, 0x3b82f6, 0xf59e0b, 0x8b5cf6];
          for (let row = 0; row < 3; row++) {
            for (let b = 0; b < 5; b++) {
              const book = new THREE.Mesh(
                new THREE.BoxGeometry(0.18, 0.5, 0.35),
                new THREE.MeshStandardMaterial({ color: bookColors[(row + b) % bookColors.length] })
              );
              book.position.set(4.8 + b * 0.3, 1.2 + row * 1.0, -6.4);
              shelfGroup.add(book);
            }
          }
          roomGroup.add(shelfGroup);

          // Terrace Railing
          const railMat = new THREE.MeshStandardMaterial({ color: 0x64748b });
          const railing = new THREE.Mesh(new THREE.BoxGeometry(4.5, 0.8, 0.1), railMat);
          railing.position.set(4.5, 0.4, 6.8);
          roomGroup.add(railing);

          // Aquarium
          const tankGeo = new THREE.BoxGeometry(1.6, 1.2, 0.7);
          const tankMat = new THREE.MeshStandardMaterial({
            color: 0x38bdf8,
            transparent: true,
            opacity: 0.6,
            roughness: 0.1
          });
          const fishTank = new THREE.Mesh(tankGeo, tankMat);
          fishTank.position.set(-6.2, 1.2, 1.5);
          fishTank.castShadow = true;
          roomGroup.add(fishTank);

          const standGeo = new THREE.BoxGeometry(1.6, 0.6, 0.7);
          const standMat = new THREE.MeshStandardMaterial({ color: 0x1e293b });
          const stand = new THREE.Mesh(standGeo, standMat);
          stand.position.set(-6.2, 0.3, 1.5);
          stand.castShadow = true;
          roomGroup.add(stand);

          // Plant #4
          const pot4 = pot1.clone();
          pot4.position.set(-5.8, 0.3, 5.0);
          roomGroup.add(pot4);
          const leaf4 = leaf1.clone();
          leaf4.position.set(-5.8, 0.85, 5.0);
          roomGroup.add(leaf4);
        }

        // Premium / Apex Tier: Golden Wealth Trophy + 5th Golden/Emerald Plant
        if (tier === 'premium') {
          const trophyGroup = new THREE.Group();
          const trophyPedestal = new THREE.Mesh(
            new THREE.CylinderGeometry(0.4, 0.5, 1.0, 8),
            new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.2, metalness: 0.3 })
          );
          trophyPedestal.position.set(0, 0.5, -5.5);
          trophyPedestal.castShadow = true;
          trophyGroup.add(trophyPedestal);

          const trophyCup = new THREE.Mesh(
            new THREE.CylinderGeometry(0.3, 0.15, 0.5, 8),
            new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.9, roughness: 0.2 })
          );
          trophyCup.position.set(0, 1.25, -5.5);
          trophyCup.castShadow = true;
          trophyGroup.add(trophyCup);

          const trophyBase = new THREE.Mesh(
            new THREE.BoxGeometry(0.3, 0.1, 0.3),
            new THREE.MeshStandardMaterial({ color: 0xd97706, metalness: 0.8, roughness: 0.3 })
          );
          trophyBase.position.set(0, 1.05, -5.5);
          trophyGroup.add(trophyBase);
          roomGroup.add(trophyGroup);

          // Plant #5 (Luxurious Golden/Emerald)
          const pot5 = pot1.clone();
          pot5.position.set(2.2, 0.3, 5.6);
          roomGroup.add(pot5);
          const leaf5 = new THREE.Mesh(
            new THREE.DodecahedronGeometry(0.65, 0),
            new THREE.MeshStandardMaterial({ color: 0x10b981, roughness: 0.5 })
          );
          leaf5.position.set(2.2, 0.95, 5.6);
          leaf5.castShadow = true;
          roomGroup.add(leaf5);
        }
      }
    }

    scene.add(roomGroup);

    // 6. User Character Creation (Habbo-inspired blocky avatar)
    const characterGroup = new THREE.Group();
    const skinColorHex = parseInt(characterCosmetics.skinTone.replace('#', '0x'), 16) || 0xf5cba7;
    const hairColorHex = parseInt(characterCosmetics.hairColor.replace('#', '0x'), 16) || 0x4a2e18;
    const shirtColorHex = parseInt(characterCosmetics.shirtColor.replace('#', '0x'), 16) || 0x3498db;
    const pantsColorHex = parseInt(characterCosmetics.pantsColor.replace('#', '0x'), 16) || 0x2c3e50;
    const shoesColorHex = parseInt(characterCosmetics.shoesColor.replace('#', '0x'), 16) || 0xffffff;

    const skinMat = new THREE.MeshStandardMaterial({ color: skinColorHex, roughness: 0.6 });
    const hairMat = new THREE.MeshStandardMaterial({ color: hairColorHex, roughness: 0.7 });
    const shirtMat = new THREE.MeshStandardMaterial({ color: shirtColorHex, roughness: 0.6 });
    const pantsMat = new THREE.MeshStandardMaterial({ color: pantsColorHex, roughness: 0.7 });
    const shoesMat = new THREE.MeshStandardMaterial({ color: shoesColorHex, roughness: 0.5 });

    // Torso (Body)
    const torsoMesh = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.85, 0.44), shirtMat);
    torsoMesh.position.y = 1.25;
    torsoMesh.castShadow = true;
    characterGroup.add(torsoMesh);

    // Head
    const headMesh = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.68, 0.68), skinMat);
    headMesh.position.y = 1.95;
    headMesh.castShadow = true;
    characterGroup.add(headMesh);

    // Hair cap
    const hairCap = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.3, 0.72), hairMat);
    hairCap.position.y = 2.22;
    characterGroup.add(hairCap);

    // Eyes
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111827 });
    const eyeL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.08, 0.02), eyeMat);
    eyeL.position.set(-0.16, 1.96, 0.35);
    characterGroup.add(eyeL);

    const eyeR = eyeL.clone();
    eyeR.position.set(0.16, 1.96, 0.35);
    characterGroup.add(eyeR);

    // Left & Right Arms
    const leftArm = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.75, 0.22), shirtMat);
    leftArm.position.set(-0.5, 1.2, 0);
    leftArm.castShadow = true;
    characterGroup.add(leftArm);

    const rightArm = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.75, 0.22), shirtMat);
    rightArm.position.set(0.5, 1.2, 0);
    rightArm.castShadow = true;
    characterGroup.add(rightArm);

    // Left & Right Legs
    const leftLeg = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.8, 0.28), pantsMat);
    leftLeg.position.set(-0.2, 0.45, 0);
    leftLeg.castShadow = true;
    characterGroup.add(leftLeg);

    const rightLeg = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.8, 0.28), pantsMat);
    rightLeg.position.set(0.2, 0.45, 0);
    rightLeg.castShadow = true;
    characterGroup.add(rightLeg);

    // Shoes
    const shoeL = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.18, 0.38), shoesMat);
    shoeL.position.set(-0.2, 0.09, 0.05);
    shoeL.castShadow = true;
    characterGroup.add(shoeL);

    const shoeR = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.18, 0.38), shoesMat);
    shoeR.position.set(0.2, 0.09, 0.05);
    shoeR.castShadow = true;
    characterGroup.add(shoeR);

    // Accessory (Glasses / Cap / Scarf)
    if (characterCosmetics.accessory === 'glasses') {
      const glassesMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
      const glasses = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.05), glassesMat);
      glasses.position.set(0, 1.96, 0.36);
      characterGroup.add(glasses);
    } else if (characterCosmetics.accessory === 'cap') {
      const capMat = new THREE.MeshStandardMaterial({ color: 0xd97706 });
      const capVisor = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.1, 0.3), capMat);
      capVisor.position.set(0, 2.22, 0.45);
      characterGroup.add(capVisor);
    }

    characterGroup.position.set(0, 0, 0);
    scene.add(characterGroup);

    // 7. Click/Tap Target Indicator (Pulsing ring on floor)
    const targetRingGeo = new THREE.RingGeometry(0.25, 0.4, 24);
    targetRingGeo.rotateX(-Math.PI / 2);
    const targetRingMat = new THREE.MeshBasicMaterial({
      color: 0x38bdf8,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0
    });
    const targetMarker = new THREE.Mesh(targetRingGeo, targetRingMat);
    targetMarker.position.set(0, 0.02, 0);
    scene.add(targetMarker);

    // Save refs for anim loop and controls
    sceneElementsRef.current = {
      scene,
      camera,
      renderer,
      characterGroup,
      targetMarker,
      charParts: {
        leftLeg,
        rightLeg,
        leftArm,
        rightArm,
        body: characterGroup
      }
    };

    // 8. Animation & Movement Loop
    let clock = new THREE.Clock();
    let walkCycle = 0;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      const elapsed = clock.getElapsedTime();

      // Read movement inputs (WASD / Arrows)
      let moveX = 0;
      let moveZ = 0;
      const keys = keysPressedRef.current;

      // Isometric diagonal projection mapping for natural keyboard controls
      if (keys['w'] || keys['arrowup']) {
        moveX -= 1;
        moveZ -= 1;
      }
      if (keys['s'] || keys['arrowdown']) {
        moveX += 1;
        moveZ += 1;
      }
      if (keys['a'] || keys['arrowleft']) {
        moveX -= 1;
        moveZ += 1;
      }
      if (keys['d'] || keys['arrowright']) {
        moveX += 1;
        moveZ -= 1;
      }

      // Check click/tap target
      const target = charTargetRef.current;
      if (target) {
        const dx = target.x - charPosRef.current.x;
        const dz = target.z - charPosRef.current.z;
        const dist = Math.hypot(dx, dz);

        if (dist > 0.2) {
          moveX = dx / dist;
          moveZ = dz / dist;
          targetMarker.visible = true;
          targetRingMat.opacity = 0.5 + 0.3 * Math.sin(elapsed * 8);
        } else {
          // Reached destination
          charTargetRef.current = null;
          targetMarker.visible = false;
        }
      } else {
        targetMarker.visible = false;
      }

      const isMoving = Math.hypot(moveX, moveZ) > 0.05;
      const speed = 4.2;

      if (isMoving) {
        // Normalize
        const len = Math.hypot(moveX, moveZ);
        const normX = moveX / len;
        const normZ = moveZ / len;

        // Update position bounded by walls
        const nextX = Math.max(-5.8, Math.min(5.8, charPosRef.current.x + normX * speed * delta));
        const nextZ = Math.max(-5.8, Math.min(5.8, charPosRef.current.z + normZ * speed * delta));
        charPosRef.current.x = nextX;
        charPosRef.current.z = nextZ;

        // Smooth rotation facing movement direction
        const targetRot = Math.atan2(normX, normZ);
        charPosRef.current.rotation = targetRot;

        // Walk cycle animation
        walkCycle += delta * 12;
        leftLeg.rotation.x = Math.sin(walkCycle) * 0.7;
        rightLeg.rotation.x = -Math.sin(walkCycle) * 0.7;
        leftArm.rotation.x = -Math.sin(walkCycle) * 0.6;
        rightArm.rotation.x = Math.sin(walkCycle) * 0.6;
        characterGroup.position.y = Math.abs(Math.sin(walkCycle * 2)) * 0.08;
      } else {
        // Idle animation (gentle breathing)
        leftLeg.rotation.x = 0;
        rightLeg.rotation.x = 0;
        leftArm.rotation.x = 0;
        rightArm.rotation.x = 0;
        characterGroup.position.y = Math.sin(elapsed * 2.5) * 0.03;
      }

      characterGroup.position.x = charPosRef.current.x;
      characterGroup.position.z = charPosRef.current.z;
      characterGroup.rotation.y = THREE.MathUtils.lerp(
        characterGroup.rotation.y,
        charPosRef.current.rotation,
        0.2
      );

      // Determine active zone for HUD
      if (showEmptyPlot) {
        setCurrentZone('Empty Ground Plot');
      } else {
        const cx = charPosRef.current.x;
        const cz = charPosRef.current.z;
        if (cz > 2) {
          setCurrentZone('Terrace & Botanical Garden');
        } else if (cx > 1) {
          setCurrentZone('Productivity Studio');
        } else {
          setCurrentZone('Living Lounge');
        }
      }

      // Smooth camera follow
      const zoom = cameraZoomRef.current;
      const desiredCamPos = new THREE.Vector3(
        14 * zoom + charPosRef.current.x * 0.25,
        18 * zoom,
        14 * zoom + charPosRef.current.z * 0.25
      );
      camera.position.lerp(desiredCamPos, 0.08);
      camera.lookAt(charPosRef.current.x * 0.4, 0.8, charPosRef.current.z * 0.4);

      renderer.render(scene, camera);
    };

    animate();
    setLoading(false);

    // Resize Handler
    const handleResize = () => {
      if (!container || !renderer || !camera) return;
      const newW = container.clientWidth;
      const newH = container.clientHeight;
      camera.aspect = newW / newH;
      camera.updateProjectionMatrix();
      renderer.setSize(newW, newH);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      scene.clear();
    };
  }, [tier, hasWealthData, publicTheme, isPublicView, characterCosmetics, publicDecorations]);

  // Click / Tap to Move Raycaster
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const { camera, targetMarker } = sceneElementsRef.current;
    if (!canvas || !camera) return;

    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

    // Intersect floor plane (Y = 0)
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const targetPoint = new THREE.Vector3();
    const hit = raycaster.ray.intersectPlane(plane, targetPoint);

    if (hit) {
      const clampedX = Math.max(-5.6, Math.min(5.6, targetPoint.x));
      const clampedZ = Math.max(-5.6, Math.min(5.6, targetPoint.z));
      charTargetRef.current = { x: clampedX, z: clampedZ };
      if (targetMarker) {
        targetMarker.position.set(clampedX, 0.02, clampedZ);
        targetMarker.visible = true;
      }
    }
  };

  // Mobile D-Pad Press
  const handleDpadPress = (direction: 'up' | 'down' | 'left' | 'right') => {
    charTargetRef.current = null;
    const step = 1.2;
    if (direction === 'up') {
      charPosRef.current.x = Math.max(-5.6, charPosRef.current.x - step * 0.7);
      charPosRef.current.z = Math.max(-5.6, charPosRef.current.z - step * 0.7);
    } else if (direction === 'down') {
      charPosRef.current.x = Math.min(5.6, charPosRef.current.x + step * 0.7);
      charPosRef.current.z = Math.min(5.6, charPosRef.current.z + step * 0.7);
    } else if (direction === 'left') {
      charPosRef.current.x = Math.max(-5.6, charPosRef.current.x - step * 0.7);
      charPosRef.current.z = Math.min(5.6, charPosRef.current.z + step * 0.7);
    } else if (direction === 'right') {
      charPosRef.current.x = Math.min(5.6, charPosRef.current.x + step * 0.7);
      charPosRef.current.z = Math.max(-5.6, charPosRef.current.z - step * 0.7);
    }
  };

  const handleZoom = (inOut: 'in' | 'out') => {
    if (inOut === 'in') {
      cameraZoomRef.current = Math.max(0.65, cameraZoomRef.current - 0.15);
    } else {
      cameraZoomRef.current = Math.min(1.4, cameraZoomRef.current + 0.15);
    }
  };

  const handleResetCamera = () => {
    cameraZoomRef.current = 1.0;
    charPosRef.current = { x: 0, z: 0, rotation: 0 };
    charTargetRef.current = null;
  };

  // WebGL Fallback View
  if (!webglSupported) {
    return (
      <div className="finance-canvas-wrapper" role="region" aria-label="3D World Fallback View">
        <div className="webgl-fallback-container">
          <div className="webgl-fallback-card">
            <svg
              className="fallback-isometric-illustration"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <polygon points="50,15 85,35 50,55 15,35" fill="#38bdf8" opacity="0.8" />
              <polygon points="15,35 50,55 50,85 15,65" fill="#0284c7" />
              <polygon points="85,35 50,55 50,85 85,65" fill="#0369a1" />
              <circle cx="50" cy="45" r="10" fill="#f5cba7" />
              <rect x="44" y="55" width="12" height="15" rx="3" fill="#3b82f6" />
            </svg>
            <h3 className="fallback-title">My Wealth 3D World (Accessible Mode)</h3>
            <p className="fallback-desc">
              3D WebGL acceleration is currently disabled or unavailable on this browser. Your full
              financial figures, summary tiers, and character data remain completely accessible and
              secure below.
            </p>
            <div className="fallback-character-summary">
              <div
                className="fallback-avatar-circle"
                style={{ backgroundColor: characterCosmetics.shirtColor || '#38bdf8' }}
              >
                👤
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#f8fafc' }}>
                  {isPublicView ? 'Public Character Profile' : 'Private World Active'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  Current Zone: {currentZone}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showEmptyPlot = !isPublicView && (!hasWealthData || tier === 'empty');
  const tierData = showEmptyPlot
    ? WEALTH_TIER_CONFIG.empty
    : WEALTH_TIER_CONFIG[tier] || WEALTH_TIER_CONFIG.starter;

  return (
    <div
      ref={containerRef}
      className="finance-canvas-wrapper"
      role="region"
      aria-label="Interactive 3D Blocky Finance House"
      tabIndex={0}
    >
      {loading && (
        <div className="canvas-loading-overlay">
          <div className="canvas-spinner" />
          <span>Building your 3D world...</span>
        </div>
      )}

      {/* Canvas Element */}
      <canvas
        ref={canvasRef}
        className="finance-canvas"
        onClick={handleCanvasClick}
        title="Click anywhere on the floor to walk your character"
      />

      {/* HUD Overlay */}
      <div className="canvas-hud">
        <div className="hud-left">
          <div className="hud-badge">
            <span
              className="tier-dot"
              style={{
                backgroundColor: isPublicView ? '#38bdf8' : showEmptyPlot ? '#10b981' : tierData.accentColor
              }}
            />
            {isPublicView
              ? `House Theme: ${publicTheme.toUpperCase()}`
              : showEmptyPlot
              ? '🌱 Empty Ground Plot'
              : `World Tier: ${tierData.name}`}
          </div>
          <div className="hud-subtext">
            {isPublicView
              ? 'Public decorative showcase. Financial data is private.'
              : showEmptyPlot
              ? 'Enter your wealth data below to build your house & unlock furnishings.'
              : tierData.description}
          </div>
          {!isMobile && (
            <div className="hud-controls-hint">
              <span>Move:</span> <kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> or <kbd>Arrows</kbd> / Click floor
            </div>
          )}
        </div>
      </div>

      {/* Floating Toolbar: Zoom, Reset, Customize */}
      <div className="canvas-floating-toolbar">
        <button
          className="canvas-tool-btn"
          onClick={() => handleZoom('in')}
          title="Zoom In"
          aria-label="Zoom camera in"
        >
          +
        </button>
        <button
          className="canvas-tool-btn"
          onClick={() => handleZoom('out')}
          title="Zoom Out"
          aria-label="Zoom camera out"
        >
          −
        </button>
        <button
          className="canvas-tool-btn"
          onClick={handleResetCamera}
          title="Reset Camera & Center Character"
          aria-label="Reset camera"
        >
          ⟲
        </button>
        {onOpenCustomizer && (
          <button
            className="canvas-tool-btn"
            onClick={onOpenCustomizer}
            title="Customize Character & Cosmetics"
            aria-label="Customize character and cosmetics"
          >
            🎨
          </button>
        )}
      </div>

      {/* Zone Indicator */}
      <div className="room-zone-pill">
        <span className="zone-dot" />
        <span>Zone: {currentZone}</span>
      </div>

      {/* Mobile Touch D-Pad */}
      {isMobile && (
        <div className="mobile-dpad-container" aria-label="Touch D-Pad movement controls">
          <button
            type="button"
            className="dpad-btn dpad-up"
            onClick={() => handleDpadPress('up')}
            aria-label="Move character up"
          >
            ▲
          </button>
          <button
            type="button"
            className="dpad-btn dpad-left"
            onClick={() => handleDpadPress('left')}
            aria-label="Move character left"
          >
            ◀
          </button>
          <div className="dpad-btn dpad-center">Move</div>
          <button
            type="button"
            className="dpad-btn dpad-right"
            onClick={() => handleDpadPress('right')}
            aria-label="Move character right"
          >
            ▶
          </button>
          <button
            type="button"
            className="dpad-btn dpad-down"
            onClick={() => handleDpadPress('down')}
            aria-label="Move character down"
          >
            ▼
          </button>
        </div>
      )}
    </div>
  );
};

export default FinanceWorldCanvas;
